import { IS_DEV } from "@/utils/env";

/**
 * @typedef {Object} ZillowPropertyRecord
 * @property {string | number=} id
 * @property {string | number=} property_id
 * @property {string=} pk
 * @property {string=} insert_date
 * @property {string=} full_address
 */

/**
 * Legacy backend payload.
 * @typedef {Object} LegacyZillowResponseBody
 * @property {ZillowPropertyRecord[]=} properties
 */

/**
 * New backend payload.
 * @typedef {Object} NewZillowResponseBody
 * @property {ZillowPropertyRecord[]=} new_properties
 * @property {ZillowPropertyRecord[]=} existing_properties
 * @property {number=} properties_processed
 */

/**
 * Normalized response shape used by UI flows.
 * @typedef {Object} AdaptedZillowResponse
 * @property {boolean} isNewApiResponse
 * @property {ZillowPropertyRecord[]} newProperties
 * @property {ZillowPropertyRecord[]} existingProperties
 * @property {ZillowPropertyRecord[]} allProperties
 * @property {number} propertiesProcessed
 * @property {string} message
 * @property {boolean} hasLinkConflict
 */

/**
 * @typedef {"success" | "info" | "warning"} ZillowToastVariant
 */

/**
 * @typedef {"new" | "existing" | "all" | "none"} ZillowPropertySelection
 */

/**
 * @typedef {Object} ZillowMessageOutcome
 * @property {ZillowToastVariant} toastVariant
 * @property {string} toastMessage
 * @property {ZillowPropertySelection} propertySelection
 * @property {boolean} shouldPrependProperties
 * @property {boolean} shouldRegisterReportsWatcher
 * @property {number} statusCode
 */

export const LINK_CONFLICT_USER_MESSAGE =
  "The property you're trying to add is already assigned to a different investor.";

export const ZILLOW_LINK_CONFLICT_STATUS_CODE = 409;

const ZILLOW_SUCCESS_STATUS_CODE = 200;

const DEFAULT_OUTCOME = Object.freeze({
  toastVariant: "success",
  propertySelection: "all",
  shouldPrependProperties: true,
  shouldRegisterReportsWatcher: true,
  statusCode: ZILLOW_SUCCESS_STATUS_CODE,
});

/**
 * Interprets the backend message as the single source of truth for UI behavior.
 * @param {string | null | undefined} rawMessage
 * @returns {ZillowMessageOutcome}
 */
export const getZillowMessageOutcome = (rawMessage) => {
  const message = typeof rawMessage === "string" ? rawMessage.trim() : "";

  if (message === "Property processing completed successfully")
    return { ...DEFAULT_OUTCOME, toastVariant: "success", toastMessage: message, propertySelection: "new", shouldPrependProperties: true };

  if (message === "Property already exists" || message === "Already exists")
    return {
      ...DEFAULT_OUTCOME,
      toastVariant: "warning",
      toastMessage: "This property is already in the system",
      propertySelection: "existing",
      shouldPrependProperties: false,
      shouldRegisterReportsWatcher: false,
    };

  if (message.startsWith("Property already exists."))
    return {
      ...DEFAULT_OUTCOME,
      toastVariant: "warning",
      toastMessage: message,
      propertySelection: "existing",
      shouldPrependProperties: false,
      shouldRegisterReportsWatcher: false,
    };

  if (message.includes("link conflicts")) {
    if (IS_DEV) {
      console.warn("[ZillowAdapter] link conflict (raw backend message):", message);
    }
    return {
      toastVariant: "warning",
      toastMessage: LINK_CONFLICT_USER_MESSAGE,
      propertySelection: "new",
      shouldPrependProperties: false,
      shouldRegisterReportsWatcher: false,
      statusCode: ZILLOW_LINK_CONFLICT_STATUS_CODE,
    };
  }

  return { ...DEFAULT_OUTCOME, toastMessage: message || "Property processing completed successfully" };
};

/**
 * Adapts both legacy and new getZillowProperty payloads into one safe shape.
 * @param {LegacyZillowResponseBody | NewZillowResponseBody | null | undefined} responseBody
 * @returns {AdaptedZillowResponse}
 */
export const adaptZillowResponse = (responseBody) => {
  if (!responseBody || typeof responseBody !== "object") {
    return {
      isNewApiResponse: false,
      newProperties: [],
      existingProperties: [],
      allProperties: [],
      propertiesProcessed: 0,
      message: "",
      hasLinkConflict: false,
    };
  }

  const isNewApi = Array.isArray(responseBody.new_properties) && Array.isArray(responseBody.existing_properties);
  const newProperties = isNewApi ? responseBody.new_properties : [];
  const existingProperties = isNewApi ? responseBody.existing_properties : [];

  let allProperties = isNewApi
    ? [...newProperties, ...existingProperties]
    : Array.isArray(responseBody.properties)
      ? responseBody.properties
      : [];

  if (!allProperties.length) {
    const comparisons = Array.isArray(responseBody.comparisons) ? responseBody.comparisons : [];
    const comps = Array.isArray(responseBody.comps) ? responseBody.comps : [];
    const topLevel = Array.isArray(responseBody) ? responseBody : [];
    allProperties = comparisons.length ? comparisons : comps.length ? comps : topLevel;
    if (!allProperties.length) console.warn("[ZillowAdapter] No properties found. Response keys:", Object.keys(responseBody));
  }

  const message = typeof responseBody.message === "string" ? responseBody.message : "";

  return {
    isNewApiResponse: isNewApi,
    newProperties,
    existingProperties,
    allProperties,
    propertiesProcessed: Number.isFinite(responseBody.properties_processed) ? responseBody.properties_processed : allProperties.length,
    message,
    hasLinkConflict: message.includes("link conflicts"),
  };
};

export default adaptZillowResponse;
